import {
  assign,
  createMachine,
  ErrorPlatformEvent,
  Interpreter,
} from 'xstate-v4'
import { UserPersonalDetailsContext, UserPersonalDetailsEditing } from './types'
import { UserPersonalDetailsRepositoryFetch } from '../UserPersonalDetailsRepositoryFetch'
import sentryCapture from '../../../utility/sentryCapture'

interface UserPersonalDetailsSchema {
  states: {
    idle: {}
    fetching: {}
    editing: {}
    submitting: {}
  }
}

type UserPersonalDetailsEvents =
  | {
      type: 'CANCEL_EDITING_PERSONAL_DETAILS'
    }
  | {
      type: 'EDIT_PERSONAL_DETAILS'
    }
  | {
      type: 'SUBMIT_PERSONAL_DETAILS'
    }
  | {
      type: 'MODIFY_EDITABLE_PERSONAL_DETAILS'
      personalDetailsEdited: Record<string, string>
    }
  | { type: 'CLEAR' }

function getInitialContextPersonalDetails(): UserPersonalDetailsContext {
  const personalDetails = {
    address: '',
    birthDate: '',
    city: '',
    country: '',
    county: '',
    email: '',
    gender: '',
    postcode: '',
    telephone: {
      countryDialingCode: '',
      countryDialingNumber: '',
      number: '',
    },
    carRegistrationNumber: '',
    typeAndApproxSizeOfUnit: '',
    unitType: { name: '', id: '', slug: '' },
    dimensionsWidth: '',
    dimensionsDepth: '',
    namesOfAllPartyMembers: '',
  }

  return {
    personalDetails: { ...personalDetails },
    errors: {},
    personalDetailsEditing: { ...personalDetails },
  }
}

export function returnUserPersonalDetailsMachine() {
  return createMachine<UserPersonalDetailsContext, UserPersonalDetailsEvents>(
    {
      id: 'UserPersonalDetails',
      context: getInitialContextPersonalDetails(),
      initial: 'fetching',
      states: {
        idle: {
          on: {
            EDIT_PERSONAL_DETAILS: 'editing',
            CLEAR: { actions: assign(getInitialContextPersonalDetails()) },
          },
        },
        fetching: {
          invoke: {
            src: 'fetchUserPersonalDetails',
            onDone: {
              actions: assign({
                personalDetails: (_, { data }) => data,
                personalDetailsEditing: (_, { data }) => data,
              }),
              target: 'idle',
            },
            onError: {
              target: 'idle',
              actions: 'logError',
            },
          },
        },
        editing: {
          on: {
            SUBMIT_PERSONAL_DETAILS: 'submitting',
            CANCEL_EDITING_PERSONAL_DETAILS: {
              target: 'idle',
              actions: assign({
                personalDetailsEditing: ({ personalDetails }) => {
                  return { ...personalDetails } as UserPersonalDetailsEditing
                },
              }),
            },
            MODIFY_EDITABLE_PERSONAL_DETAILS: {
              actions: assign({
                personalDetailsEditing: (
                  { personalDetailsEditing },
                  { personalDetailsEdited },
                ) => {
                  return {
                    ...personalDetailsEditing,
                    ...personalDetailsEdited,
                  }
                },
              }),
            },
          },
        },
        submitting: {
          invoke: {
            src: 'submitUserPersonalDetails',
            onError: {
              target: 'editing',
              actions: [
                'logError',
                assign({
                  errors: (_, { data }) => data,
                }),
              ],
            },
            onDone: [
              {
                target: 'editing',
                actions: assign({
                  errors: (_, { data }) => {
                    return data.errors
                  },
                }),
                cond: (_, { data }) => data && !!data.errors,
              },
              {
                target: 'fetching',
                actions: assign({
                  errors: (_) => ({}),
                }),
              },
            ],
          },
        },
      },
    },
    {
      services: {
        submitUserPersonalDetails: ({ personalDetailsEditing }) => {
          const repo = new UserPersonalDetailsRepositoryFetch()
          return repo.submit(personalDetailsEditing)
        },
        fetchUserPersonalDetails: () => {
          const repo = new UserPersonalDetailsRepositoryFetch()
          return repo.get()
        },
      },
      actions: {
        logError: (_, _event: any) => {
          const event: ErrorPlatformEvent = _event
          event.data.name =
            'userPersonalDetailsMachine ' + event.type + ' error'
          sentryCapture(event.data)
        },
      },
    },
  )
}

export type UserPersonalDetailsMachineInterpreter = Interpreter<
  UserPersonalDetailsContext,
  UserPersonalDetailsSchema,
  UserPersonalDetailsEvents
>
